import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ShareIcon from '@mui/icons-material/Share';
import ForwardIcon from '@mui/icons-material/Forward';
import { useNavigate, useParams } from 'react-router-dom';
import { getLaboklinApi } from '../../services/laboklinApi';
import type { LabReport } from '../../types/laboklin';
import { LABOKLIN_RED } from '../../theme/laboklinTheme';
import TestResultsTable from './TestResultsTable';

/**
 * A single report, matching the MyLab detail screen: status line, the portal's
 * own action row, a details block and the collapsible results accordion.
 *
 * The additions are the abnormal-count line, the clinical-signs row and
 * "Interpret with GekkoVet". Everything else is deliberately the portal as it
 * exists, including actions that are inert here.
 */

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}.${m}.${y}` : iso;
}

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', py: 0.25 }}>
    <Typography sx={{ width: 200, flexShrink: 0 }}>{label}:</Typography>
    <Typography>{value}</Typography>
  </Box>
);

interface ReportDetailViewProps {
  onInterpret: (reportNo: string) => void;
}

export const ReportDetailView: React.FC<ReportDetailViewProps> = ({ onInterpret }) => {
  const { reportNo } = useParams<{ reportNo: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<LabReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!reportNo) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    getLaboklinApi()
      .getReport(reportNo)
      .then((data) => {
        if (!cancelled) setReport(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reportNo]);

  if (loading) {
    return (
      <Paper sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  if (error || !report) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error ?? 'Report not found.'}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/mylab')}>
          Back
        </Button>
      </Box>
    );
  }

  const inertActions = [
    { label: 'Download as PDF', icon: <PictureAsPdfIcon sx={{ fontSize: 18 }} /> },
    { label: 'Download as LDT', icon: <DescriptionIcon sx={{ fontSize: 18 }} /> },
    { label: 'Forward results', icon: <ForwardIcon sx={{ fontSize: 18 }} /> },
    { label: 'Share report', icon: <ShareIcon sx={{ fontSize: 18 }} /> },
  ];

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Typography>
          <strong>Status:</strong>{' '}
          <Box component="span" sx={{ color: '#2e7d32' }}>
            {report.reportType ?? 'Final'}
          </Box>
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {inertActions.map((a) => (
            <Box
              key={a.label}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: LABOKLIN_RED, fontSize: 15 }}
            >
              {a.icon}
              {a.label}
            </Box>
          ))}
          <Button variant="contained" color="primary" onClick={() => onInterpret(report.reportNo)}>
            Interpret with GekkoVet
          </Button>
        </Box>
      </Box>

      <Typography variant="h3" sx={{ mb: 1.5 }}>
        Report details
      </Typography>

      <Box sx={{ mb: 3 }}>
        <DetailRow label="Report number" value={report.reportNo} />
        <DetailRow label="Received date" value={formatDate(report.receiveDate)} />
        <DetailRow label="Report date" value={formatDate(report.reportDate)} />
        <DetailRow label="Pet owner" value={report.patient.owner ?? ''} />
        <DetailRow label="Patient" value={report.patient.name ?? ''} />
        <DetailRow label="Breed" value={report.patient.breed ?? ''} />
        <DetailRow label="Species" value={report.patient.species ?? ''} />
        <DetailRow label="Gender" value={report.patient.sex ?? ''} />
        {/* The laboratory prints this on the report, so the portal shows it too -
            and it is the history the assistant now works from. */}
        {report.clinicalSigns && <DetailRow label="Clinical signs" value={report.clinicalSigns} />}
      </Box>

      <Accordion defaultExpanded disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#f8f9fa' }}>
          <Typography sx={{ fontWeight: 500 }}>
            Test results
            {report.abnormalCount > 0 && (
              <Typography component="span" sx={{ color: 'text.secondary', ml: 1, fontSize: 14 }}>
                — {report.abnormalCount} outside the reference range
              </Typography>
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TestResultsTable results={report.results} />
        </AccordionDetails>
      </Accordion>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate('/mylab')}>
          Back
        </Button>
      </Box>
    </Paper>
  );
};

export default ReportDetailView;
